/**
 * SuggestionDropdown Component
 *
 * Displays filter suggestions in a dropdown menu
 */

import React, { useRef, useEffect } from 'react';
import {
  TagIcon,
  HashtagIcon,
  DocumentTextIcon,
  GlobeAltIcon,
  LinkIcon,
  ChevronRightIcon,
} from '@heroicons/react/20/solid';
import { SuggestionDropdownProps, FilterSuggestion } from '../types';
import { highlightMatch } from '../engine/suggestionEngine';

/**
 * Get icon for a filter type
 */
const getFilterIcon = (filterId: string): React.ReactNode => {
  const iconClass = 'h-4 w-4 text-text-tertiary';

  switch (filterId) {
    case 'nodeType':
      return <TagIcon className={iconClass} />;
    case 'nodeId':
      return <HashtagIcon className={iconClass} />;
    case 'name':
      return <DocumentTextIcon className={iconClass} />;
    case 'domain':
      return <GlobeAltIcon className={iconClass} />;
    case 'edge':
      return <LinkIcon className={iconClass} />;
    default:
      return <TagIcon className={iconClass} />;
  }
};

/**
 * Single suggestion item
 */
interface SuggestionItemProps {
  suggestion: FilterSuggestion;
  isHighlighted: boolean;
  query: string;
  onSelect: () => void;
  onMouseEnter: () => void;
}

const SuggestionItem: React.FC<SuggestionItemProps> = ({
  suggestion,
  isHighlighted,
  query,
  onSelect,
  onMouseEnter,
}) => {
  const itemRef = useRef<HTMLDivElement>(null);
  const isCategory = suggestion.isCategory;

  // Scroll highlighted item into view
  useEffect(() => {
    if (isHighlighted && itemRef.current) {
      itemRef.current.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    }
  }, [isHighlighted]);

  // Highlight matching text in label
  const highlightedLabel = highlightMatch(suggestion.label, query);

  return (
    <div
      ref={itemRef}
      className={`
        flex items-center gap-3 px-3 py-2.5 cursor-pointer
        ${isHighlighted
          ? 'bg-accent-indigo/15'
          : 'hover:bg-bg-3/50'
        }
        ${isCategory ? 'font-medium' : ''}
        transition-colors duration-100
      `}
      onClick={onSelect}
      onMouseEnter={onMouseEnter}
      role="option"
      aria-selected={isHighlighted}
    >
      {/* Icon */}
      <div className="flex-shrink-0">
        {getFilterIcon(suggestion.filterId)}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Label with highlighted text */}
        <div className="text-sm text-text-primary">
          {highlightedLabel.map((segment, i) => (
            <span
              key={i}
              className={segment.highlighted ? 'text-accent-gold font-semibold' : ''}
            >
              {segment.text}
            </span>
          ))}
        </div>

        {/* Description - only show for non-categories or when there's a specific match */}
        {suggestion.description && !isCategory && (
          <div className="text-xs text-text-tertiary truncate mt-0.5">
            {suggestion.description}
          </div>
        )}
      </div>

      {/* Chevron for categories, "Configure..." for other configurable items */}
      {isCategory ? (
        <ChevronRightIcon className="h-4 w-4 text-text-tertiary flex-shrink-0" />
      ) : suggestion.needsConfiguration ? (
        <div className="flex-shrink-0 text-xs text-text-tertiary">
          Configure...
        </div>
      ) : null}
    </div>
  );
};

/**
 * SuggestionDropdown Component
 *
 * @param suggestions - Suggestions to display
 * @param definitions - Filter definitions for labels
 * @param highlightedIndex - Currently highlighted index
 * @param onSelect - Callback when suggestion is selected
 * @param isVisible - Whether dropdown is visible
 * @param query - Current search query (for highlighting)
 * @param onHighlightChange - Callback when highlight changes (mouse hover)
 */
export const SuggestionDropdown: React.FC<
  SuggestionDropdownProps & {
    query?: string;
    onHighlightChange?: (index: number) => void;
  }
> = ({
  suggestions,
  highlightedIndex,
  onSelect,
  isVisible,
  query = '',
  onHighlightChange,
}) => {
  if (!isVisible || suggestions.length === 0) {
    return null;
  }

  return (
    <div
      id="filter-suggestions"
      className="
        absolute z-50 mt-1 w-full
        bg-bg-1/95 backdrop-blur-md
        border border-light
        rounded-xl shadow-2xl
        max-h-[320px] overflow-y-auto
        py-1
      "
      role="listbox"
      aria-label="Filter suggestions"
    >
      {suggestions.map((suggestion, index) => (
        <SuggestionItem
          key={`${suggestion.filterId}-${suggestion.label}-${index}`}
          suggestion={suggestion}
          isHighlighted={index === highlightedIndex}
          query={query}
          onSelect={() => onSelect(suggestion)}
          onMouseEnter={() => onHighlightChange?.(index)}
        />
      ))}

      {/* Keyboard hint */}
      <div className="
        border-t border-light
        px-3 py-1.5 text-xs text-text-tertiary
        flex items-center justify-between
      ">
        <span>
          <kbd className="px-1 py-0.5 bg-bg-3/60 border border-light/40 rounded text-[10px]">↑↓</kbd>
          {' '}to navigate
        </span>
        <span>
          <kbd className="px-1 py-0.5 bg-bg-3/60 border border-light/40 rounded text-[10px]">Enter</kbd>
          {' '}to select
        </span>
        <span>
          <kbd className="px-1 py-0.5 bg-bg-3/60 border border-light/40 rounded text-[10px]">Esc</kbd>
          {' '}to close
        </span>
      </div>
    </div>
  );
};

export default SuggestionDropdown;
